import dayjs, { Dayjs } from 'dayjs';
import { GraphData, DeviceData, Device, Sensor, Calibration } from '../../../Types/types';
import { LocalstorageData } from '../../../Types/LocalStorageData';
import { isEnergySavingTool } from '../../../utils/typeUtils';
import { ChartOptions } from '../context/interfaces';
import { noCacheFetcher } from '../../../utils/fetch';

function constructUrl(deviceId: string, fromDate?: Dayjs) {
  let url = process.env.REACT_APP_API_URL + `initGraph/${encodeURIComponent(deviceId)}`;
  if (fromDate) {
    url += `?from=${fromDate.toISOString()}`;
  }
  return url;
}

export function initGraph(
  deviceId: string,
  from?: Dayjs
): Promise<{ sensors: Sensor[]; calibrations: Calibration[]; deviceData: DeviceData[] }> {
  return new Promise((resolve, reject) => {
    noCacheFetcher(constructUrl(deviceId, from))
      .then((res) => {
        if (res.status !== 200) {
          return reject('Could not initialize graph');
        } else {
          res
            .json()
            .then((response) => {
              return resolve(response);
            })
            .catch((err) => {
              return reject('Could not initialize graph');
            });
        }
      })
      .catch((err) => {
        return reject('Could not initialize graph');
      });
  });
}

export function unAccumulateGraphData(data: GraphData[]) {
  const newData = [...data];
  for (let i = newData.length - 1; i > 0; i--) {
    newData[i][1] = newData[i][1] - newData[i - 1][1];
  }
  if (newData.length > 0) newData[0][1] = 0;
  return newData;
}

export function filterESTLabels(labels: string[]) {
  const validPowerDeviceLabels = ['kwh_delta_sum', 'battery', 'connectivity', 'temperature'];
  return labels.filter((label) => validPowerDeviceLabels.includes(label.toLowerCase()));
}

export function filterDataByDateRange(data: DeviceData[], chartOptions: ChartOptions) {
  //Ensuring we go from start to the end of the day
  const fromCpy = chartOptions.fromDate
    ? chartOptions.fromDate.startOf('day')
    : dayjs().subtract(1, 'day').startOf('day');

  const toCpy = chartOptions.toDate ? chartOptions.toDate.endOf('day') : dayjs().endOf('day');

  // Compute the timezone offset
  let filtered = data // - set me as const again when removing the temporary fix below!
    //Filter based on chosen dates and labels
    .filter((item) => {
      const itemDate = dayjs(new Date(item.time));
      const isNewerThanFrom = itemDate.isAfter(fromCpy);
      const isOlderThanTo = itemDate.isBefore(toCpy);
      const isSensor = item.sensor === chartOptions.sensor;
      return isNewerThanFrom && isOlderThanTo && isSensor;
    });

  // -- Start of terrible temporary fix
  // TODO: This is a temporary fix until the data is fixed on the devices
  // This should be removed as soon as the data is fixed
  // ... or not, who knows ¯\_(ツ)_/¯
  // Just so we're clear: Laurits is not a fan of this. He is not responsible for
  // any issues that should occur because of this. 
  // Note - when removing this, remember to set "filtered" as a const again
  // - thanks :-)
  if (chartOptions.sensor === 'temperature') {
    filtered = filtered.filter((item: DeviceData) => parseFloat(item.value) < 100.0);
  }
  // -- End of terrible temporary fix

  //Only take out 1000 elements, otherwise the graph can't show it (maybe more than 1000 is ok, try)
  // .slice(-1000)
  //Accumulate values if the user choose to
  const reduced = filtered.reduce((accumulator, currentData) => {
    // const prevValue = accumulate && index > 0 ? accumulator[index - 1].y : 0;
    const currentValue = parseFloat(currentData.value);

    //If a calibration is chosen, then compute it
    const yValueFinal =
      chartOptions.calibration && chartOptions.calibration.value
        ? eval(chartOptions.calibration.value.replaceAll('$', `${currentValue}`))
        : currentValue;

    accumulator.push([
      new Date(currentData.time).getTime(), // - tzOffset,
      yValueFinal,
    ]);

    return accumulator; // Highcharts needs the data to be in ascending order
  }, [] as GraphData[]);

  return reduced.sort((a, b) => a[0] - b[0]);
}

export function initIsPowerDevice(deviceId: string, devices: Device[]) {
  return isEnergySavingTool(devices.find((d) => d.id === deviceId)?.deviceType);
}

// This function is for checking if we reached the fetch limit
export const calcNewFromDate = (
  data: DeviceData[],
  fromDate: Dayjs,
  openSnackbar?: (severity: 'success' | 'error' | 'warning', message: string) => void,
  limit: number = 25_000
) => {
  //check if the last (oldest) element has the same timestamp as requested
  const oldestElement = data.length > 0 ? data[0].time : new Date().toDateString();
  const oldestElementDate = dayjs(new Date(oldestElement));
  if (fromDate.isBefore(oldestElementDate) && data.length === limit) {
    if (openSnackbar)
      openSnackbar(
        'warning',
        `You can at most fetch ${limit} data points at a time. So we went back to: ${oldestElementDate.format('YYYY-MM-DD HH:mm:ss')}`
      );
    return oldestElementDate;
  } else {
    return fromDate;
  }
};

export function findOldestDataDate(data: DeviceData[], sensor: string, historicData: DeviceData[]) {
  let oldestDate = dayjs(new Date());
  const deviceDataSensor = data.filter((item) => item.sensor === sensor);
  const historicSensorData = [...historicData, ...deviceDataSensor];
  if (historicSensorData.length > 0) {
    oldestDate = dayjs(new Date(historicSensorData[0].time)).subtract(1, 'millisecond');
  }
  return oldestDate;
}

export function getOldData(
  device: string,
  chartOptions: ChartOptions,
  deviceData: DeviceData[]
): Promise<{ data: DeviceData[]; fromDate: Dayjs } | { error: string }> {
  return new Promise((resolve, reject) => {
    const fromDate = chartOptions.fromDate;
    const toDate = chartOptions.toDate;
    const sensor = chartOptions.sensor;
    if (!fromDate || !toDate || !sensor) {
      return reject({ error: 'fromDate, toDate or sensor is missing' });
    }

    const url =
      process.env.REACT_APP_API_URL +
      `getolddata/${encodeURIComponent(device)}/${sensor}?from=${fromDate.toISOString()}&to=${toDate.toISOString()}`;
    noCacheFetcher(url, {
      method: 'GET',
      headers: new Headers({
        // Your header content
        'Content-Type': 'application/json',
      }),
    }).then((res) => {
      if (res.status !== 200) {
        return resolve({ error: 'Could not fetch more data' });
      }
      res
        .json()
        .then((response: DeviceData[]) => {
          const data = response;

          const sortedData = data.sort((a, b) => {
            return Math.floor(new Date(a.time).getTime() / 1000) - Math.floor(new Date(b.time).getTime() / 1000);
          });

          const newFromDate = calcNewFromDate(sortedData, fromDate);

          if (deviceData === undefined || deviceData.length === 0) {
            return resolve({ data: sortedData, fromDate: newFromDate });
          } else {
            const earliestDayInLocalStorage = dayjs(deviceData[0].time);
            const newHistoricData: DeviceData[] = [];
            for (const data of sortedData) {
              if (dayjs(data.time).isBefore(earliestDayInLocalStorage)) {
                newHistoricData.push(data);
              }
            }
            return resolve({ data: newHistoricData, fromDate: newFromDate });
          }
        })
        .catch((err) => {
          console.error(err);
          return reject({ error: 'Could not fetch more data' });
        });
    });
  });
}
